/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { BorderOutlined, CheckSquareFilled } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { useSpareConfirmState } from '../../hooks/useSpareConfirmState'
import type { AttendancePlayer } from '../../types'
import { DEMOTE_TOOLTIP } from '../../utils/statusCopy'

interface ConfirmCheckboxProps {
  player: AttendancePlayer
}

const ConfirmCheckbox: React.FC<ConfirmCheckboxProps> = ({ player }) => {
  const {
    confirmed,
    confirmDisabled,
    canDePromote,
    canDePromoteNow,
    onToggleConfirm,
    onDePromote,
  } = useSpareConfirmState(player)

  const onPress = confirmDisabled
    ? undefined
    : canDePromote
    ? onDePromote
    : onToggleConfirm

  if (!confirmed) {
    return (
      <BorderOutlined
        css={iconStyle(confirmDisabled)}
        style={{ color: colors.NEUTRAL_100 }}
        onClick={onPress}
      />
    )
  }

  return (
    // Only a pressable de-promote checkbox gets a tooltip; the display-only
    // ones have no action to describe.
    <Tooltip
      title={canDePromoteNow ? DEMOTE_TOOLTIP : ''}
      zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}
    >
      <CheckSquareFilled
        css={iconStyle(confirmDisabled)}
        style={{ color: colors.INFO_300 }}
        aria-label={canDePromoteNow ? DEMOTE_TOOLTIP : undefined}
        onClick={onPress}
      />
    </Tooltip>
  )
}

/** CONFIRM column (Roster Spares). */
const ConfirmCell = ({ data }: ICellRendererParams<AttendancePlayer>) =>
  data ? <ConfirmCheckbox player={data} /> : null

export default ConfirmCell

const iconStyle = (disabled: boolean) => css`
  font-size: 24px;
  cursor: ${disabled ? 'default' : 'pointer'};
`
